import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LANGS, DEFAULT_LANG, useLang, parsePath, localizePath } from "@/i18n";

/**
 * SEO metadata + <html lang> + canonical + hreflang manager.
 * Direct DOM writes (no react-helmet dep) — the app has one page mounted
 * at a time so this stays lean.
 *
 * Usage:
 *   <PageMeta titleKey="meta.home_title" descriptionKey="meta.home_description" path="/" />
 */
const SITE_BASE =
  (typeof window !== "undefined" && window.__IAG_SITE_URL__) ||
  process.env.REACT_APP_SITE_URL ||
  "https://interactglobally.com.br";

const upsertMeta = (attr, name, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, hrefLang, href) => {
  const selector = hrefLang
    ? `link[rel="${rel}"][hreflang="${hrefLang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hrefLang) el.setAttribute("hreflang", hrefLang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const clearAlternates = () => {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((n) => n.remove());
};

export const PageMeta = ({ titleKey, descriptionKey, path, image, noindex = false }) => {
  const { t, lang } = useLang();
  const { pathname } = useLocation();

  useEffect(() => {
    const title = t(titleKey);
    const description = t(descriptionKey);
    const currentLangEntry = LANGS.find((l) => l.code === lang) || LANGS[0];
    const canonicalPath = path || parsePath(pathname).rest;

    // <title> + description + html lang
    document.title = title;
    document.documentElement.lang = currentLangEntry.htmlLang;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large");

    // Canonical (pointing to the current language variant)
    const canonicalUrl = SITE_BASE + localizePath(canonicalPath, lang);
    upsertLink("canonical", null, canonicalUrl);

    // Hreflang alternates for every language + x-default (EN)
    clearAlternates();
    LANGS.forEach((l) => {
      const href = SITE_BASE + localizePath(canonicalPath, l.code);
      upsertLink("alternate", l.htmlLang, href);
    });
    upsertLink(
      "alternate",
      "x-default",
      SITE_BASE + localizePath(canonicalPath, DEFAULT_LANG)
    );

    // Open Graph + Twitter (minimum viable)
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:locale", currentLangEntry.ogLocale);
    upsertMeta("property", "og:site_name", t("meta.site_name"));
    if (image) upsertMeta("property", "og:image", image);
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
  }, [titleKey, descriptionKey, path, image, noindex, t, lang, pathname]);

  return null;
};

export default PageMeta;
